import ReactDOM from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/hooks/useRedux';
import { setCurrentPlan } from '@/redux/planSlice';
import useClickOutside from '@/hooks/useClickOutside';
import arrowDown from '@/assets/icons/arrow-down.svg';
import NewTagIcon from '@/assets/icons/new-tag.svg?react';
import useResetStates from '@/hooks/useResetStates';
import useRoutes from '@/hooks/useRoutes';
import usePlan from '@/hooks/usePlan';
import plans from '@/database/plans';
import styles from './selectors.module.scss';
import { resetBookingOriginDestination, resetBookingStates, setFunnelType } from '@/redux/bookingSlice';
import { resetTravellersStates } from '@/redux/travellersSlice';
import { FunnelType } from '@/types/types';

const itemList = plans
    .toSorted((a, b) => a.order - b.order)
    .filter((plan) => plan.active)
    .map((plan) => {
        return {
            id: plan.id,
            title: plan.title,
            icon: plan.planIcon,
        };
    });

const aloyPlan = plans
    .filter((plan) => plan.id === 9)!
    .map((plan) => {
        return {
            id: plan.id,
            title: plan.title,
            icon: plan.planIcon,
        };
    });

const PlanSelector = () => {
    const [isActive, setIsActive] = useState(false);
    const selectorRef = useRef<HTMLDivElement>(null);
    const { currentPathname, experiencesInfoRoutes, isHomePage, routes } = useRoutes();
    const { planConfig } = usePlan();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { resetGlobalStates } = useResetStates();
    const { t } = useTranslation();

    const isPlanInfoPage = experiencesInfoRoutes.includes(currentPathname);

    const onSelectorClick = () => {
        setIsActive(!isActive);
    };

    const onItemClick = (plan: number) => {
        const aloyPlanIsSelected = plan === 9;
        resetGlobalStates();
        dispatch(setCurrentPlan(plan));
        setIsActive(false);

        if (isPlanInfoPage) {
            const destinationUrl = experiencesInfoRoutes[plan];
            navigate(destinationUrl);
        }

        if (aloyPlanIsSelected) {
            dispatch(resetBookingStates());
            dispatch(setFunnelType(FunnelType.explore));
            dispatch(resetBookingOriginDestination());
            dispatch(resetTravellersStates());
            navigate(routes.aloyHome);
        }
    };

    useClickOutside(selectorRef, () => {
        if (isActive) setIsActive(false);
    });

    const itemListToShow = itemList.filter((plan) => plan.id !== planConfig.id);
    // const plansWithAloy = [...itemListToShow, ...aloyPlan]; Desactivar ALOY del selector
    const plansWithAloy = [...itemListToShow];
    const plansToShow = isHomePage ? plansWithAloy : itemListToShow;
    const root = document.getElementById('root')!;

    const planSelectorZIndex = isActive ? { zIndex: 99999 } : {};
    const planGradient = `linear-gradient(220deg, ${planConfig.colors.dark}, ${planConfig.colors.light})`;

    return (
        <>
            <div
                className={`${styles.selectorContainer} ${styles.planSelector}`}
                style={planSelectorZIndex}
                ref={selectorRef}
            >
                <p className={styles.selectorName}>{t('experience')}</p>
                <button className={styles.selector} style={{ background: planGradient }} onClick={onSelectorClick}>
                    <img
                        className={`${styles.icon} ${styles.iconActive} ${
                            planConfig.id === 10 ? styles.christmasIcon : null
                        }`}
                        src={planConfig.planIcon}
                        alt=""
                    />
                    <p className={`${styles.text} ${styles.textActive}`}>{t(planConfig.title)}</p>

                    <div className={`${styles.arrow} ${styles.arrowWhite}`}>
                        <img src={arrowDown} alt="" className={isActive ? styles.arrowActive : ''} />
                    </div>
                </button>
                {isActive && <SelectorOptions itemsList={plansToShow} onItemClick={onItemClick} />}
            </div>

            {isActive && ReactDOM.createPortal(<div className={styles.backgroundOpacity} />, root)}
        </>
    );
};

interface IOptionsProps {
    itemsList: any;
    onItemClick: any;
}

const SelectorOptions = ({ itemsList, onItemClick }: IOptionsProps) => {
    const selectorOptionsRef = useRef<any>(null);
    const { t } = useTranslation();

    useEffect(() => {
        addButtonsAnimation();
    }, []);

    const addButtonsAnimation = () => {
        const buttons = selectorOptionsRef.current.getElementsByTagName('button');
        let time = 50;
        Array.from(buttons).forEach((button: any) => {
            setTimeout(() => {
                button.classList.add(styles.appearAnimation);
            }, time);
            time += 50;
        });
    };

    return (
        <div className={styles.planSelectorOptions} ref={selectorOptionsRef}>
            <ul>
                {itemsList?.map((item: any, index: number) => {
                    const isAloyPlan = item.id === 9;
                    return (
                        <li key={item.id}>
                            <button onClick={() => onItemClick(item.id)} tabIndex={index}>
                                <img
                                    className={`${styles.icon} ${item.id === 10 ? styles.christmasIcon : ''} ${
                                        item.id === 11 ? styles.valentinesDayIcon : null
                                    }`}
                                    src={item.icon}
                                    alt=""
                                />
                                {t(item.title)}
                                {isAloyPlan && <NewTagIcon className={styles.tagNew} />}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PlanSelector;
